import { pgTable, text, varchar, uuid, timestamp, integer, date, pgEnum, boolean } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

// Cross-domain imports
import { deals } from "./schema-deals.js";
import { contacts, accounts } from "./schema-crm.js";

// ============================================================================
// DOCUMENT HANDLING MODULE - SPRINT-17
// Deal document collection workflow (Phase 2) and entity document library (Phase 3)
// PRD Reference: docs/prds/PRD-DOCUMENTS.md (planned)
// ============================================================================

// ============================================================================
// ENUMS
// NOTE: The following enums already exist in the baseline schema (DO NOT recreate):
//   document_applicant_role, document_entity_scope, document_loan_type,
//   document_verification_level, document_requirement_level
// They are defined here purely for Drizzle type-safety; the DO/EXCEPTION block
// in the migration makes the SQL idempotent.
// ============================================================================

// Existing baseline enums — Drizzle definitions for type-safety
export const documentApplicantRoleEnum = pgEnum("document_applicant_role", [
  "borrower",
  "guarantor",
  "director",
  "trustee",
  "member",
  "beneficiary",
  "any_individual",
]);

export const documentEntityScopeEnum = pgEnum("document_entity_scope", [
  "borrower",
  "individual",
  "both",
]);

export const documentLoanTypeEnum = pgEnum("document_loan_type", [
  "home",
  "business",
  "commercial",
  "smsf",
]);

export const documentVerificationLevelEnum = pgEnum("document_verification_level", [
  "full_doc",
  "alt_doc",
  "low_doc",
  "no_doc",
]);

export const documentRequirementLevelEnum = pgEnum("document_requirement_level", [
  "required",
  "conditional",
  "optional",
]);

const MAX_DOCUMENT_FILE_SIZE_BYTES = 25 * 1024 * 1024;

function isSafeDocumentFileName(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (value[i] === "/" || value[i] === "\\" || code <= 31 || code === 127) {
      return false;
    }
  }
  return true;
}

const documentFileNameSchema = z
  .string()
  .min(1)
  .max(255)
  .refine(isSafeDocumentFileName, {
    message: "File name must not contain path separators or control characters",
  });

const documentFileTypeSchema = z.enum([
  "application/pdf",
  "image/png",
  "image/jpeg",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);

const documentFileSizeSchema = z
  .number()
  .int()
  .positive()
  .max(MAX_DOCUMENT_FILE_SIZE_BYTES);

// New enum: document_collection_status (SPRINT-17)
export const documentCollectionStatusEnum = pgEnum("document_collection_status", [
  "outstanding",
  "requested",
  "received",
  "verified",
  "rejected",
  "linked",
]);

// ============================================================================
// TABLES
// ============================================================================

// ── Reference Tables ────────────────────────────────────────────────────────
// These tables hold the document type catalogue (seed data, not user-generated).
// Defined here for Drizzle ORM type safety; tables were created by Sprint 17 seed migration.

export const documentCategories = pgTable("crm_x_document_categories", {
  id: uuid("id").primaryKey().defaultRandom(),
  code: varchar("code", { length: 50 }).notNull(),
  name: varchar("name", { length: 100 }).notNull(),
  description: text("description"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});

export const documentTypes = pgTable("crm_x_document_types", {
  id: uuid("id").primaryKey().defaultRandom(),
  categoryId: uuid("category_id").references(() => documentCategories.id).notNull(),
  code: varchar("code", { length: 100 }).notNull(),
  name: varchar("name", { length: 200 }).notNull(),
  entityScope: documentEntityScopeEnum("entity_scope"),
  description: text("description"),
  isRequired: boolean("is_required").default(false),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});

// Entity-level document library — reusable documents attached to accounts/contacts
// Must be defined before dealDocuments because of the linked_entity_document_id FK
export const entityDocuments = pgTable("crm_x_entity_documents", {
  id: uuid("id").primaryKey().defaultRandom(),
  // Existing nullable column from the organisation isolation migration.
  organisationId: uuid("organisation_id"),
  accountId: uuid("account_id").references(() => accounts.id, { onDelete: "cascade" }),
  contactId: uuid("contact_id").references(() => contacts.id, { onDelete: "cascade" }),
  documentTypeId: uuid("document_type_id").notNull().references(() => documentTypes.id),
  fileName: varchar("file_name", { length: 255 }).notNull(),
  fileSize: integer("file_size"),
  fileType: varchar("file_type", { length: 50 }),
  fileUrl: text("file_url"),
  documentDate: date("document_date"),
  validFrom: date("valid_from"),
  validUntil: date("valid_until"),
  isCurrent: boolean("is_current").notNull().default(true),
  // superseded_by is a self-referential FK — defined as plain uuid; Drizzle circular refs
  // would require a forward-reference which is unsupported; DB FK enforced in migration
  supersededBy: uuid("superseded_by"),
  lastVerifiedAt: timestamp("last_verified_at", { withTimezone: true }),
  lastVerifiedBy: uuid("last_verified_by"),
  notes: text("notes"),
  uploadedAt: timestamp("uploaded_at", { withTimezone: true }).notNull().defaultNow(),
  uploadedBy: uuid("uploaded_by"),
});

// Per-deal document tracking with collection status
export const dealDocuments = pgTable("crm_x_deal_documents", {
  id: uuid("id").primaryKey().defaultRandom(),
  // Existing nullable column from the organisation isolation migration.
  organisationId: uuid("organisation_id"),
  dealId: uuid("deal_id").notNull().references(() => deals.id, { onDelete: "cascade" }),
  documentTypeId: uuid("document_type_id").notNull().references(() => documentTypes.id),
  accountId: uuid("account_id").references(() => accounts.id, { onDelete: "set null" }),
  contactId: uuid("contact_id").references(() => contacts.id, { onDelete: "set null" }),
  applicantRole: documentApplicantRoleEnum("applicant_role").notNull().default("borrower"),
  collectionStatus: documentCollectionStatusEnum("collection_status").notNull().default("outstanding"),
  requestedAt: timestamp("requested_at", { withTimezone: true }),
  receivedAt: timestamp("received_at", { withTimezone: true }),
  verifiedAt: timestamp("verified_at", { withTimezone: true }),
  verifiedBy: uuid("verified_by"),
  rejectionReason: text("rejection_reason"),
  fileName: varchar("file_name", { length: 255 }),
  fileSize: integer("file_size"),
  fileType: varchar("file_type", { length: 50 }),
  fileUrl: text("file_url"),
  // linked_entity_document_id FK to crm_x_entity_documents — added via ALTER TABLE in migration
  linkedEntityDocumentId: uuid("linked_entity_document_id"),
  lenderRequirementId: uuid("lender_requirement_id"),
  quantityRequired: varchar("quantity_required", { length: 50 }),
  maxAgeDays: integer("max_age_days"),
  documentDate: date("document_date"),
  expiryDate: date("expiry_date"),
  versionNumber: integer("version_number").default(1),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  createdBy: uuid("created_by"),
});

// ============================================================================
// ZOD SCHEMAS
// ============================================================================

export const insertEntityDocumentSchema = createInsertSchema(entityDocuments, {
  documentTypeId: z.string().uuid(),
  accountId: z.string().uuid().optional().nullable(),
  contactId: z.string().uuid().optional().nullable(),
  fileName: documentFileNameSchema,
  fileSize: documentFileSizeSchema.optional().nullable(),
  fileType: documentFileTypeSchema.optional().nullable(),
  supersededBy: z.string().uuid().optional().nullable(),
  lastVerifiedBy: z.string().uuid().optional().nullable(),
  uploadedBy: z.string().uuid().optional().nullable(),
}).omit({ organisationId: true });

export const selectEntityDocumentSchema = createSelectSchema(entityDocuments);

export const updateEntityDocumentSchema = insertEntityDocumentSchema.partial();

export const insertDealDocumentSchema = createInsertSchema(dealDocuments, {
  dealId: z.string().uuid(),
  documentTypeId: z.string().uuid(),
  accountId: z.string().uuid().optional().nullable(),
  contactId: z.string().uuid().optional().nullable(),
  verifiedBy: z.string().uuid().optional().nullable(),
  linkedEntityDocumentId: z.string().uuid().optional().nullable(),
  lenderRequirementId: z.string().uuid().optional().nullable(),
  createdBy: z.string().uuid().optional().nullable(),
  fileName: documentFileNameSchema.optional().nullable(),
  fileSize: documentFileSizeSchema.optional().nullable(),
  fileType: documentFileTypeSchema.optional().nullable(),
  // Timestamp overrides: JSON serialisation sends ISO strings, not Date objects
  requestedAt: z.coerce.date().optional().nullable(),
  receivedAt: z.coerce.date().optional().nullable(),
  verifiedAt: z.coerce.date().optional().nullable(),
}).omit({ organisationId: true });

export const selectDealDocumentSchema = createSelectSchema(dealDocuments);

export const updateDealDocumentSchema = insertDealDocumentSchema.partial();

export const insertDocumentCategorySchema = createInsertSchema(documentCategories);
export const selectDocumentCategorySchema = createSelectSchema(documentCategories);

export const insertDocumentTypeSchema = createInsertSchema(documentTypes, {
  categoryId: z.string().uuid().optional().nullable(),
  entityScope: z.enum(["borrower", "individual", "both"]).optional().nullable(),
});
export const selectDocumentTypeSchema = createSelectSchema(documentTypes);

// ============================================================================
// TYPESCRIPT TYPES
// ============================================================================

export type EntityDocument = typeof entityDocuments.$inferSelect;
export type InsertEntityDocument = typeof entityDocuments.$inferInsert;

export type DealDocument = typeof dealDocuments.$inferSelect;
export type InsertDealDocument = typeof dealDocuments.$inferInsert;

export type DocumentCategory = typeof documentCategories.$inferSelect;
export type InsertDocumentCategory = typeof documentCategories.$inferInsert;

export type DocumentType = typeof documentTypes.$inferSelect;
export type InsertDocumentType = typeof documentTypes.$inferInsert;
